import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AccountAssignmentResult, ClientAccount } from '../api/accountAssignmentApi';
import { useClientAccounts } from '../api/accountAssignmentApi';
import { useDeleteLessonMutation, useDuplicateLessonMutation } from '../api/courseApi';
import {
  EMPTY_LESSON_COURSE_USAGE,
  useLessonList,
  useLessonUsageQuery,
  type LessonListResponseItem,
} from '../api/courseRecordApi';
import { AccountAssignmentPanel } from '../components/AccountAssignmentPanel';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';
import { Toast } from '../components/Toast';
import { LessonUsagePanel } from '../components/LessonUsagePanel';
import { ListPagination } from '../components/table/ListPagination';
import { SortableHeader } from '../components/table/SortableHeader';
import {
  TagFilterChipRow,
  TagFilterControl,
  TagNameList,
  describeActiveTagFilters,
  type TagFilterOption,
} from '../components/tagFilter/TagFilterControl';
import { useTagsQuery } from '../api/tagApi';
import { EditTagsDialog } from '../components/tagPicker/EditTagsDialog';
import { useListControls, usePageBoundsReset } from '../hooks/useListControls';
import { useRememberListPath } from '../hooks/useListPathMemory';
import { useToast } from '../hooks/useToast';
import { WorkflowStatusBadge, deriveBadgeState } from '../components';
import { useEditorStore } from '../store/editorStore';
import { formatLessonHumanId } from '../utils/humanIds';
import { useClientContext } from '../hooks/useClientContext';
import { canDelete, describeDeleteBlock } from '../utils/deleteEligibility';
import { StandardTitleLine } from '../components/StandardTitleLine';
import {
  AdminIcon,
  AdminPageHeader,
  AdminRowMenu,
  AdminStatusMessage,
  adminActionIcons,
  createAdminListStyles,
  useAdminNarrowViewport,
} from '../admin-ui';

function formatAssignedTo(ids: string[], accounts: ClientAccount[] | undefined): string {
  if (ids.length === 0) return '0 accounts';
  if (ids.length === 1) return accounts?.find((a) => a.id === ids[0])?.name ?? '1 account';
  return `${ids.length} accounts`;
}

type SortKey = 'id' | 'title' | 'workflowState' | 'createdAt';

const LESSON_SORT_KEYS: readonly SortKey[] = ['id', 'title', 'workflowState', 'createdAt'];

function formatLessonCreatedAt(createdAt: string): string {
  if (!createdAt) return '—';

  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '—';

  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${date.getUTCDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function LessonListPage() {
  const navigate = useNavigate();
  useRememberListPath('/lessons');
  const { clientContext } = useClientContext();
  const isStandardContext = clientContext === null;
  const { data: clientAccounts } = useClientAccounts();
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  // The kebab trigger outlives the menu item that opens a dialog, so it is the
  // only stable focus target: opening a dialog unmounts the clicked item in the
  // same commit, and ModalShell would otherwise capture <body> and restore
  // focus nowhere on close.
  const kebabTriggerRef = useRef<HTMLButtonElement | null>(null);
  const duplicateDialogTitleId = useId();
  const deleteReasonIdPrefix = useId();
  const isNarrow = useAdminNarrowViewport();
  const lessonListStyles = useMemo(() => createAdminListStyles({ narrow: isNarrow }), [isNarrow]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [duplicateConfirmId, setDuplicateConfirmId] = useState<string | null>(null);
  const [lessonActionError, setLessonActionError] = useState<string | null>(null);
  const [openActionLessonId, setOpenActionLessonId] = useState<string | null>(null);
  const [editTagsLesson, setEditTagsLesson] = useState<{ id: string; title: string } | null>(null);
  const { message: toastMessage, showToast } = useToast();
  const {
    searchInput,
    setSearchInput,
    sortKey,
    sortDir,
    handleSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    tagIds,
    setTagIds,
    queryParams,
    hasNonDefaultState,
    resetToDefaults,
  } = useListControls<SortKey>({
    defaultSort: 'createdAt',
    sortKeys: LESSON_SORT_KEYS,
    assignedAccountId: clientContext?.id,
    sortStorageKey: '/lessons',
  });
  const { data: tagLibrary } = useTagsQuery({ assignedAccountId: clientContext?.id });
  const tagFilterOptions: TagFilterOption[] = useMemo(
    () => (tagLibrary?.items ?? []).map((tag) => ({ id: tag.id, name: tag.name, usageCount: tag.lessonCount })),
    [tagLibrary],
  );
  const [assignmentPanel, setAssignmentPanel] = useState<{
    id: string;
    title: string;
    initialFilter: 'notAssigned' | 'assigned';
    isPublished: boolean;
  } | null>(null);
  const [usagePanelLesson, setUsagePanelLesson] = useState<{ id: string; title: string } | null>(null);

  const {
    data: lessonPage,
    isLoading: isLoadingLessons,
    isPlaceholderData,
  } = useLessonList(queryParams);

  const lessons = lessonPage?.items ?? [];
  const totalCount = lessonPage?.totalCount ?? 0;

  usePageBoundsReset({ page, pageSize, totalCount, isPlaceholderData, setPage });

  useEffect(() => {
    if (!openActionLessonId) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (
        actionMenuRef.current &&
        event.target instanceof Node &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setOpenActionLessonId(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [openActionLessonId]);

  const { mutate: deleteLesson, isPending: isDeleting } = useDeleteLessonMutation({
    onSuccess: () => {
      setDeleteConfirmId(null);
      setLessonActionError(null);
    },
    onError: (error) => {
      // The mutation refreshes eligibility on a refusal, so the row this
      // banner refers to comes back disabled with its reason.
      setLessonActionError(error.message || 'Failed to delete lesson');
      setDeleteConfirmId(null);
    },
  });

  const resetEditorPreviewState = useCallback(() => {
    const editorState = useEditorStore.getState();
    editorState.resetPreviewProgress();
    editorState.setPreviewTargetBlockId(null);
    editorState.setReturnTargetBlockId(null);
    editorState.setPreviewCurrentLocation(null);
    editorState.setIsPreviewMode(false);
    return editorState;
  }, []);

  const { mutate: duplicateLesson, isPending: isDuplicating } = useDuplicateLessonMutation({
    onSuccess: (duplicatedLesson) => {
      resetEditorPreviewState();
      setLessonActionError(null);
      setDuplicateConfirmId(null);
      setOpenActionLessonId(null);
      navigate(`/lessons/${duplicatedLesson.id}`);
    },
    onError: (error) => {
      setLessonActionError(error.message || 'Failed to duplicate lesson');
      setDuplicateConfirmId(null);
      setOpenActionLessonId(null);
    },
  });

  const handleAddLesson = useCallback(() => {
    setLessonActionError(null);
    navigate('/lessons/new');
  }, [navigate]);

  const handleOpenLessonEditor = useCallback((lessonId: string) => {
    resetEditorPreviewState();
    setLessonActionError(null);
    setOpenActionLessonId(null);
    navigate(`/lessons/${lessonId}`);
  }, [navigate, resetEditorPreviewState]);

  const handlePreviewLesson = useCallback((lessonId: string) => {
    const editorState = resetEditorPreviewState();
    editorState.setPreviewCurrentLocation(lessonId);
    editorState.setIsPreviewMode(true);
    setLessonActionError(null);
    setOpenActionLessonId(null);
    navigate(`/lessons/${lessonId}?mode=preview`);
  }, [navigate, resetEditorPreviewState]);

  const handleDuplicateClick = useCallback((lessonId: string) => {
    setOpenActionLessonId(null);
    setLessonActionError(null);
    setDuplicateConfirmId(lessonId);
  }, []);

  const handleConfirmDuplicate = useCallback(() => {
    if (!duplicateConfirmId || isDuplicating) return;

    setLessonActionError(null);
    duplicateLesson(duplicateConfirmId);
  }, [duplicateConfirmId, duplicateLesson, isDuplicating]);

  const handleCancelDuplicate = useCallback(() => {
    if (isDuplicating) return;
    setDuplicateConfirmId(null);
  }, [isDuplicating]);

  const handleDeleteClick = useCallback((lessonId: string) => {
    // Focus the trigger BEFORE the menu unmounts, so the dialog captures a
    // live element to hand focus back to.
    kebabTriggerRef.current?.focus();
    setOpenActionLessonId(null);
    setLessonActionError(null);
    setDeleteConfirmId(lessonId);
  }, []);

  const handleEditTagsClick = useCallback((lesson: LessonListResponseItem) => {
    // Same focus hand-off as Delete: the trigger outlives the menu, so the
    // dialog captures it as its focus-restore target.
    kebabTriggerRef.current?.focus();
    setOpenActionLessonId(null);
    setLessonActionError(null);
    setEditTagsLesson({ id: lesson.id, title: lesson.title || 'Untitled Lesson' });
  }, []);

  const handleTagsSaved = useCallback(({ changed }: { changed: boolean }) => {
    const title = editTagsLesson?.title;
    setEditTagsLesson(null);
    if (changed && title) showToast(`Tags updated for ${title}.`);
  }, [editTagsLesson, showToast]);

  const handleAssignToAccounts = useCallback((
    lesson: LessonListResponseItem,
    initialFilter: 'notAssigned' | 'assigned' = 'notAssigned'
  ) => {
    setOpenActionLessonId(null);
    setLessonActionError(null);
    setAssignmentPanel({
      id: lesson.id,
      title: lesson.title || 'Untitled Lesson',
      initialFilter,
      isPublished: lesson.workflowState === 'published',
    });
  }, []);

  const handleAssignedAccounts = useCallback((
    _lessonId: string,
    _result: AccountAssignmentResult
  ) => {
  }, []);

  const handleUsageCellClick = useCallback((lesson: LessonListResponseItem) => {
    setUsagePanelLesson({ id: lesson.id, title: lesson.title || 'Untitled Lesson' });
  }, []);

  const usagePanelQuery = useLessonUsageQuery(
    usagePanelLesson?.id ?? null,
    { enabled: Boolean(usagePanelLesson) },
  );
  const usagePanelUsage = usagePanelQuery.data ?? EMPTY_LESSON_COURSE_USAGE;

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirmId) deleteLesson(deleteConfirmId);
  }, [deleteConfirmId, deleteLesson]);

  const handleCancelDelete = useCallback(() => {
    if (isDeleting) return;
    setDeleteConfirmId(null);
  }, [isDeleting]);

  const lessonToDuplicate = duplicateConfirmId
    ? lessons.find((lesson) => lesson.id === duplicateConfirmId)
    : null;
  const lessonToDelete = deleteConfirmId
    ? lessons.find((lesson) => lesson.id === deleteConfirmId)
    : null;

  return (
    <>
      <div style={lessonListStyles.page}>
        <div style={lessonListStyles.stickyTop}>
          <AdminPageHeader
            title="Lessons"
            description={isStandardContext ? 'Create, preview, tag and assign lessons.' : undefined}
            actions={isStandardContext && (
              <button
                type="button"
                onClick={handleAddLesson}
                style={lessonListStyles.addLessonButton}
              >
                + New Lesson
              </button>
            )}
          />

          <div style={lessonListStyles.listToolbar}>
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search ID, title, or description..."
              style={lessonListStyles.filterInput}
              aria-label="Search lessons by ID, title, or description"
            />
            <TagFilterControl options={tagFilterOptions} selectedTagIds={tagIds} onChange={setTagIds} />
            <span style={lessonListStyles.resultsCount}>
              {totalCount} {totalCount === 1 ? 'result' : 'results'}
            </span>
            {hasNonDefaultState && (
              <button type="button" onClick={resetToDefaults} style={lessonListStyles.resetFiltersButton}>
                Reset
              </button>
            )}
          </div>

          <TagFilterChipRow options={tagFilterOptions} selectedTagIds={tagIds} onChange={setTagIds} />

          {lessonActionError && (
            <AdminStatusMessage tone="error" role={null}>{lessonActionError}</AdminStatusMessage>
          )}
        </div>

        <section style={lessonListStyles.panel}>
          {isLoadingLessons ? (
            <p style={lessonListStyles.stateText}>Loading lessons...</p>
          ) : lessons.length === 0 ? (
            <p style={lessonListStyles.stateText}>
              {tagIds.length > 0
                ? `No lessons match the selected tags (${describeActiveTagFilters(tagFilterOptions, tagIds)}). `
                : isStandardContext
                  ? 'No lessons yet. Create one to get started.'
                  : `There are no lessons assigned to ${clientContext?.name}.`}
              {tagIds.length > 0 && (
                <button type="button" onClick={() => setTagIds([])} style={lessonListStyles.resetFiltersButton}>
                  Clear filters
                </button>
              )}
            </p>
          ) : (
            <div style={lessonListStyles.tableScroll}>
              <table style={lessonListStyles.table}>
                <thead>
                  <tr>
                    <SortableHeader
                      label="ID"
                      sortKey="id"
                      activeKey={sortKey}
                      direction={sortDir}
                      onSort={handleSort}
                      styles={lessonListStyles}
                    />
                    <SortableHeader
                      label="Title"
                      sortKey="title"
                      activeKey={sortKey}
                      direction={sortDir}
                      onSort={handleSort}
                      styles={lessonListStyles}
                    />
                    <SortableHeader
                      label="Lifecycle"
                      sortKey="workflowState"
                      activeKey={sortKey}
                      direction={sortDir}
                      onSort={handleSort}
                      styles={lessonListStyles}
                    />
                    {/* NA-284: base-course impact awareness (which standard
                        courses a lesson feeds) — standard context only, like
                        the base-composition concerns it informs. */}
                    {isStandardContext && (
                      <th style={lessonListStyles.headerCell}>Used In</th>
                    )}
                    {isStandardContext && (
                      <th style={lessonListStyles.headerCell}>Assigned To</th>
                    )}
                    <SortableHeader
                      label="Created"
                      sortKey="createdAt"
                      activeKey={sortKey}
                      direction={sortDir}
                      onSort={handleSort}
                      styles={lessonListStyles}
                    />
                    {/* NA-398: only while a tag filter is active — an unconditional
                        column would be noise for the normal, unfiltered view. */}
                    {tagIds.length > 0 && (
                      <th style={lessonListStyles.headerCell}>Tags</th>
                    )}
                    <th
                      style={lessonListStyles.headerCellActions}
                      aria-label="Actions"
                    />
                  </tr>
                </thead>
                <tbody>
                  {lessons.map((lesson) => (
                    <tr key={lesson.id} style={lessonListStyles.tableRow}>
                      <td style={lessonListStyles.idCell}>{formatLessonHumanId(lesson.numericId, lesson.id)}</td>
                      <td style={lessonListStyles.bodyCell}>
                        <button
                          type="button"
                          // NA-210: draft-assigned content in a client context is
                          // view-only — the title opens Preview, not the editor.
                          onClick={() => (!isStandardContext && lesson.workflowState === 'draft'
                            ? handlePreviewLesson(lesson.id)
                            : handleOpenLessonEditor(lesson.id))}
                          style={lessonListStyles.titleButton}
                        >
                          {lesson.title || 'Untitled Lesson'}
                        </button>
                        <StandardTitleLine standardTitle={lesson.standardTitle} style={lessonListStyles.standardTitleLine} />
                      </td>
                      <td style={lessonListStyles.bodyCell}>
                        <WorkflowStatusBadge state={deriveBadgeState(lesson.workflowState, lesson.publishLifecycle)} />
                      </td>
                      {isStandardContext && (
                        <td style={lessonListStyles.bodyCell}>
                          {lesson.courseUsageCount > 0 ? (
                            <button
                              type="button"
                              onClick={() => handleUsageCellClick(lesson)}
                              style={lessonListStyles.accountLinkButton}
                              disabled={isPlaceholderData}
                            >
                              {lesson.courseUsageCount} {lesson.courseUsageCount === 1 ? 'course' : 'courses'}
                            </button>
                          ) : '—'}
                        </td>
                      )}
                      {isStandardContext && (
                        <td style={lessonListStyles.bodyCell}>
                          <button
                            type="button"
                            onClick={() => handleAssignToAccounts(
                              lesson,
                              lesson.assignedAccountIds.length > 0 ? 'assigned' : 'notAssigned'
                            )}
                            style={lessonListStyles.accountLinkButton}
                          >
                            {formatAssignedTo(lesson.assignedAccountIds, clientAccounts)}
                          </button>
                        </td>
                      )}
                      <td style={lessonListStyles.bodyCell}>{formatLessonCreatedAt(lesson.createdAt)}</td>
                      {tagIds.length > 0 && (
                        <td style={lessonListStyles.bodyCell}>
                          <TagNameList tags={lesson.tags ?? []} />
                        </td>
                      )}
                      <td style={lessonListStyles.actionsCell}>
                        <div
                          style={lessonListStyles.actionMenuWrapper}
                          ref={openActionLessonId === lesson.id ? actionMenuRef : null}
                        >
                          <button
                            type="button"
                            onClick={(event) => {
                              kebabTriggerRef.current = event.currentTarget;
                              setOpenActionLessonId((current) => current === lesson.id ? null : lesson.id);
                            }}
                            style={lessonListStyles.actionMenuButton}
                            aria-label={`Open actions for ${lesson.title || 'Untitled Lesson'}`}
                            aria-expanded={openActionLessonId === lesson.id}
                          >
                            <AdminIcon icon={adminActionIcons.more} />
                          </button>
                          {openActionLessonId === lesson.id && (
                            <AdminRowMenu style={lessonListStyles.actionMenuPopover}>
                              <button
                                type="button"
                                onClick={() => handleOpenLessonEditor(lesson.id)}
                                disabled={!isStandardContext && lesson.workflowState === 'draft'}
                                style={{
                                  ...lessonListStyles.actionMenuItem,
                                  ...(!isStandardContext && lesson.workflowState === 'draft'
                                    ? lessonListStyles.actionMenuItemDisabled
                                    : {}),
                                }}
                              >
                                Edit
                              </button>
                              {isStandardContext && (
                                <button
                                  type="button"
                                  onClick={() => handleDuplicateClick(lesson.id)}
                                  disabled={isDuplicating}
                                  style={{
                                    ...lessonListStyles.actionMenuItem,
                                    ...(isDuplicating ? lessonListStyles.actionMenuItemDisabled : {}),
                                  }}
                                >
                                  {isDuplicating && openActionLessonId === lesson.id ? 'Duplicating...' : 'Duplicate'}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handlePreviewLesson(lesson.id)}
                                style={lessonListStyles.actionMenuItem}
                              >
                                Preview
                              </button>
                              {isStandardContext && (
                                <button
                                  type="button"
                                  onClick={() => handleAssignToAccounts(lesson, 'notAssigned')}
                                  style={lessonListStyles.actionMenuItem}
                                >
                                  Assign to Accounts
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleEditTagsClick(lesson)}
                                style={lessonListStyles.actionMenuItem}
                              >
                                Edit tags
                              </button>
                              {isStandardContext && (() => {
                                // NA-283: Delete stays visible when ineligible so
                                // the reason is discoverable up front, instead of
                                // surfacing as a 409 after the author commits.
                                const blockedReason = describeDeleteBlock(lesson.deleteEligibility);
                                const deletable = canDelete(lesson.deleteEligibility);
                                return (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteClick(lesson.id)}
                                    disabled={!deletable}
                                    title={blockedReason ?? undefined}
                                    aria-describedby={blockedReason ? `${deleteReasonIdPrefix}-${lesson.id}` : undefined}
                                    style={{
                                      ...lessonListStyles.actionMenuItem,
                                      ...lessonListStyles.actionMenuItemDanger,
                                      ...(deletable ? {} : lessonListStyles.actionMenuItemDisabled),
                                    }}
                                  >
                                    Delete
                                    {blockedReason && (
                                      <span
                                        id={`${deleteReasonIdPrefix}-${lesson.id}`}
                                        style={lessonListStyles.actionMenuItemReason}
                                      >
                                        {blockedReason}
                                      </span>
                                    )}
                                  </button>
                                );
                              })()}
                            </AdminRowMenu>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <ListPagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          isPlaceholderData={isPlaceholderData}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      <DeleteConfirmModal
        presentation="admin"
        isOpen={deleteConfirmId !== null}
        title="Delete Lesson?"
        itemName={lessonToDelete?.title || 'Untitled Lesson'}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {duplicateConfirmId && (
        <div style={lessonListStyles.modalOverlay}>
          <div
            style={lessonListStyles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby={duplicateDialogTitleId}
          >
            <h3 style={lessonListStyles.modalTitle} id={duplicateDialogTitleId}>Duplicate Lesson?</h3>
            <p style={lessonListStyles.modalText}>
              Create a copy of "{lessonToDuplicate?.title || 'Untitled Lesson'}" with the title{' '}
              <strong>Copy - {lessonToDuplicate?.title || 'Untitled Lesson'}</strong>?
            </p>
            <div style={lessonListStyles.modalActions}>
              <button
                type="button"
                onClick={handleCancelDuplicate}
                style={lessonListStyles.cancelButton}
                disabled={isDuplicating}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDuplicate}
                style={{
                  ...lessonListStyles.confirmActionButton,
                  ...(isDuplicating ? lessonListStyles.confirmDeleteButtonDisabled : {}),
                }}
                disabled={isDuplicating}
                autoFocus
              >
                {isDuplicating ? 'Duplicating...' : 'Duplicate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {assignmentPanel && (
        <AccountAssignmentPanel
          contentType="lesson"
          contentId={assignmentPanel.id}
          title={assignmentPanel.title}
          initialFilter={assignmentPanel.initialFilter}
          isPublished={assignmentPanel.isPublished}
          onClose={() => setAssignmentPanel(null)}
          onAssigned={(result) => handleAssignedAccounts(assignmentPanel.id, result)}
        />
      )}

      <EditTagsDialog
        isOpen={editTagsLesson !== null}
        contentType="lesson"
        itemId={editTagsLesson?.id ?? ''}
        itemTitle={editTagsLesson?.title ?? ''}
        onClose={() => setEditTagsLesson(null)}
        onSaved={handleTagsSaved}
      />

      <Toast message={toastMessage} />

      <LessonUsagePanel
        isOpen={Boolean(usagePanelLesson)}
        onClose={() => setUsagePanelLesson(null)}
        lessonTitle={usagePanelLesson?.title ?? ''}
        usage={usagePanelUsage}
        isLoading={Boolean(usagePanelLesson) && usagePanelQuery.isPending}
        isError={usagePanelQuery.isError}
        onRetry={() => { void usagePanelQuery.refetch(); }}
      />
    </>
  );
}
